import * as Sentry from "@sentry/vue";
import {DEBUG_COOKIE} from "~/libs/constants/constants";

export enum Severity {
  Debug = 'debug',
  Info = 'info',
  Log = 'log',
  Warning = 'warning',
  Error = 'error',
  Fatal = 'fatal'
}

const logLevels: Record<Severity, Severity[]> = {
  [Severity.Debug]: [Severity.Debug, Severity.Info,Severity.Log, Severity.Warning, Severity.Error, Severity.Fatal],
  [Severity.Info]: [ Severity.Info, Severity.Log, Severity.Warning, Severity.Error, Severity.Fatal],
  [Severity.Log]: [Severity.Log, Severity.Warning, Severity.Error, Severity.Fatal],
  [Severity.Warning]: [Severity.Warning, Severity.Error, Severity.Fatal],
  [Severity.Error]: [Severity.Error, Severity.Fatal],
  [Severity.Fatal]: [Severity.Fatal],
}
export default class FrontendLogger {
  meta: Record<string, any> = {}
  tags: Record<string, any> = {}
  maxLevel: Severity = Severity.Warning

  constructor(parent?: FrontendLogger) {
    if (parent) {
      this.meta = {...parent.meta}
      this.tags = {...parent.tags}
      this.maxLevel = parent.maxLevel
    }
  }

  setLevel(level: Severity) {
    this.maxLevel = level
  }

  addMeta(key: string, value: any) {
    this.meta[key] = value
  }

  addTag(key: string, value: string): FrontendLogger {
    const newLogged = new FrontendLogger(this)
    newLogged.tags[key] = value
    return newLogged
  }

  debug(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Debug,
    })
  }

  error(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Error,
    })
  }

  fatal(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Fatal,
    })
  }

  info(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Info,
    })
  }

  trace(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Info,
    })
  }

  warn(msg: string | object | Error, ...args: any[]) {
    this.captureMessage({
      args,
      msg,
      type: Severity.Warning,
    })
  }

  captureMessage(log: {
    msg: string | object | Error
    args: any[]
    type: Severity
  }): void {
    const { msg } = log

    // Check max allowed log level
    if (!logLevels[this.maxLevel].includes(log.type)) {
      const debugCookie = useCookie(DEBUG_COOKIE)
      if (debugCookie) {
        // Log to console
        console.log(msg, log.args)
      }
      return
    }

    // Log to console
    console.log(msg, log.args)

    // log to sentry
    Sentry &&
    Sentry.withScope((scope) => {
      for (let prop in this.tags) {
        if (this.tags.hasOwnProperty(prop)) {
          scope.setTag(String(prop), String(this.tags[prop]))
        }
      }

      scope.setExtra('args', log.args)
      scope.setExtra('meta', this.meta)
      scope.setExtra('flattenLog', this.flattenObject(log))

      if (msg.constructor === Error) {
        return Sentry.captureException(msg)
      }

      Sentry.captureMessage(log.msg as string, log.type)
    })
  }

  flattenObject(ob: any) {
    try {
      const toReturn: Record<string, object> = {}
      for (const iteratorOb in ob) {
        // eslint-disable-next-line no-prototype-builtins
        if (!ob.hasOwnProperty(iteratorOb)) {
          continue
        }
        if (typeof ob[iteratorOb] === 'object' && ob[iteratorOb] !== null) {
          const flatObject = this.flattenObject(ob[iteratorOb])
          for (const iteratorObFlat in flatObject) {
            // eslint-disable-next-line no-prototype-builtins
            if (!flatObject.hasOwnProperty(iteratorObFlat)) {
              continue
            }
            toReturn[`${iteratorOb}.${iteratorObFlat}`] =
              flatObject[iteratorObFlat]
          }
        } else {
          toReturn[iteratorOb] = ob[iteratorOb]
        }
      }
      return toReturn
    } catch (e) {
      return null
    }
  }
}